import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import {
  PRIORITIES,
  DEFAULT_DOMAIN_SLUG,
  type TaskCreate,
  type Priority,
} from '@agentflow/shared';

export function NewTaskPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  const profiles = useQuery({ queryKey: ['profiles'], queryFn: () => api.listProfiles() });
  const domains = useQuery({ queryKey: ['domains'], queryFn: () => api.listDomains() });

  const meId = me.data?.user.id ?? '';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [domain, setDomain] = useState(DEFAULT_DOMAIN_SLUG);
  const [priority, setPriority] = useState<Priority>('3-medium');
  const [profileSlug, setProfileSlug] = useState<string>('plan-and-review');
  const [assignee, setAssignee] = useState<string>('');
  const [reviewer, setReviewer] = useState<string>('');

  // Default assignee to the current user once we know who they are. Use a
  // ref-style guard so we only do this once — if the user clears the field
  // we don't keep refilling it.
  const [assigneeInitialized, setAssigneeInitialized] = useState(false);
  if (meId && !assigneeInitialized) {
    setAssignee(meId);
    setAssigneeInitialized(true);
  }

  const create = useMutation({
    mutationFn: (input: TaskCreate) => api.createTask(input),
    onSuccess: ({ task }) => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      navigate(`/tasks/${task.id}`);
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!meId) return;
    create.mutate({
      name,
      description,
      domain,
      priority,
      profile_slug: profileSlug,
      owner_id: meId,
      assignee_id: assignee.trim() || undefined,
      reviewer_id: reviewer.trim() || undefined,
      initial_context: [],
    });
  };

  return (
    <div className="max-w-2xl">
      <h2 className="text-2xl font-semibold tracking-tight mb-1">New task</h2>
      <p className="text-sm text-muted-foreground mb-6">
        Fill in the basics. You can add context (files, repos, references) after creating it.
      </p>

      <form onSubmit={submit}>
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                required
                maxLength={200}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="What needs to happen?"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Details, context, success criteria…"
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Domain</Label>
                <Select value={domain} onValueChange={setDomain}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {domains.data?.domains.map((d) => (
                      <SelectItem key={d.slug} value={d.slug}>
                        {d.name}
                      </SelectItem>
                    ))}
                    {/* If the current selection isn't in the registry (e.g. an
                        old freeform value), still surface it. */}
                    {domain && !domains.data?.domains.some((d) => d.slug === domain) && (
                      <SelectItem value={domain}>{domain} (unregistered)</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  <Link to="/domains" className="underline hover:text-foreground">
                    Manage domains
                  </Link>
                </p>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={priority} onValueChange={(v) => setPriority(v as Priority)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Profile</Label>
              <Select value={profileSlug} onValueChange={setProfileSlug}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {profiles.data?.profiles.map((p) => (
                    <SelectItem key={p.slug} value={p.slug}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {profiles.data?.profiles.find((p) => p.slug === profileSlug)?.description && (
                <p className="text-xs text-muted-foreground">
                  {profiles.data.profiles.find((p) => p.slug === profileSlug)?.description}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="assignee">Assignee</Label>
                <Input
                  id="assignee"
                  type="email"
                  value={assignee}
                  onChange={(e) => setAssignee(e.target.value)}
                  placeholder="Leave blank to leave unassigned"
                />
                <p className="text-xs text-muted-foreground">
                  Who should act on this task next. Defaults to you.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="reviewer">Reviewer</Label>
                <Input
                  id="reviewer"
                  type="email"
                  value={reviewer}
                  onChange={(e) => setReviewer(e.target.value)}
                  placeholder="Leave blank to use the assignee"
                />
                <p className="text-xs text-muted-foreground">
                  Used when the profile pauses for review. Falls back to assignee, then owner.
                </p>
              </div>
            </div>

            <p className="text-xs text-muted-foreground border-t pt-3">
              Owner is always you ({meId || '—'}) and cannot be reassigned. Assignee and reviewer
              can be edited later.
            </p>
          </CardContent>
        </Card>

        <div className="mt-4 flex items-center gap-3">
          <Button type="submit" disabled={!name || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create task'}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          {create.isError && (
            <span className="text-sm text-destructive">Failed: {String(create.error)}</span>
          )}
        </div>
      </form>
    </div>
  );
}
